"use strict";
/**
 * Copyright 2014-present Palantir Technologies
 * @license MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Color = void 0;
var tslib_1 = require("tslib");
var d3 = require("d3");
var Utils = require("../utils");
var scale_1 = require("./scale");
/**
 * Workaround for bad d3 behavior.
 *
 * d3's color scales, which are oridinal scales, have side-effects when invoked.
 * When you call the `scale(value)` as a function, it will implicitly add new
 * values to the domain.
 *
 * These side-effects cause us to rely on looking up the current scale state
 * with the `.domain()` and `.range()` methods. However, these methods have poor
 * performance implications since they will always slice their internal values
 * before returning. Inside the inner render loop of color scatter plot points,
 * these slices add up.
 */
var ImplicitSeriesTracker = /** @class */ (function () {
    function ImplicitSeriesTracker() {
        this.count = 0;
        this.tracker = {};
    }
    ImplicitSeriesTracker.prototype.getIndex = function (value) {
        if (this.tracker[value] != null) {
            return this.tracker[value];
        }
        else {
            var idx = this.count;
            this.tracker[value] = idx;
            this.count += 1;
            return idx;
        }
    };
    ImplicitSeriesTracker.prototype.clear = function () {
        this.count = 0;
        this.tracker = {};
    };
    return ImplicitSeriesTracker;
}());
var Color = /** @class */ (function (_super) {
    (0, tslib_1.__extends)(Color, _super);
    /**
     * A Color Scale maps string values to color hex values expressed as a string.
     *
     * @constructor
     * @param {string} [scaleType] One of "Category10"/"Category20"/"Category20b"/"Category20c".
     *   (see https://github.com/mbostock/d3/wiki/Ordinal-Scales#categorical-colors)
     *   If not supplied, reads the colors defined using CSS -- see plottable.css.
     */
    function Color(scaleType) {
        var _this = _super.call(this) || this;
        // Cache the number of range value to avoid calling `scale.range().length`
        _this._rangeLength = 1;
        _this._tracker = new ImplicitSeriesTracker();
        var scale;
        switch (scaleType) {
            case null:
            case undefined:
                if (Color._plottableColorCache == null) {
                    Color._plottableColorCache = Color._getPlottableColors();
                }
                scale = d3.scaleOrdinal().range(Color._plottableColorCache);
                break;
            case "Category10":
            case "category10":
            case "10":
                scale = d3.scaleOrdinal(d3.schemeCategory10);
                break;
            case "Category20":
            case "category20":
            case "20":
                scale = d3.scaleOrdinal(d3.schemeCategory20);
                break;
            case "Category20b":
            case "category20b":
            case "20b":
                scale = d3.scaleOrdinal(d3.schemeCategory20b);
                break;
            case "Category20c":
            case "category20c":
            case "20c":
                scale = d3.scaleOrdinal(d3.schemeCategory20c);
                break;
            default:
                throw new Error("Unsupported ColorScale type");
        }
        _this._d3Scale = scale;
        _this._rangeLength = _this._d3Scale.range().length;
        return _this;
    }
    Color.prototype.extentOfValues = function (values) {
        return Utils.Array.uniq(values);
    };
    // Duplicated from OrdinalScale._getExtent - should be removed in #388
    Color.prototype._getExtent = function () {
        return Utils.Array.uniq(this._getAllIncludedValues());
    };
    Color.invalidateColorCache = function () {
        Color._plottableColorCache = null;
    };
    Color._getPlottableColors = function () {
        var plottableDefaultColors = [];
        var colorTester = d3.select("body").append("plottable-color-tester");
        var defaultColorHex = Utils.Color.colorTest(colorTester, "");
        var i = 0;
        var colorHex = Utils.Color.colorTest(colorTester, "plottable-colors-0");
        while (colorHex != null && i < this._MAXIMUM_COLORS_FROM_CSS) {
            if (colorHex === defaultColorHex && colorHex === plottableDefaultColors[plottableDefaultColors.length - 1]) {
                break;
            }
            plottableDefaultColors.push(colorHex);
            i++;
            colorHex = Utils.Color.colorTest(colorTester, "plottable-colors-" + i);
        }
        colorTester.remove();
        return plottableDefaultColors;
    };
    /**
     * Returns the color-string corresponding to a given string.
     *
     * If there are not enough colors in the range(), a lightened version of an
     * existing color will be used.
     *
     * @param {string} value
     * @returns {string}
     */
    Color.prototype.scale = function (value) {
        var color = this._d3Scale(value);
        var index = this._tracker.getIndex(value);
        var numLooped = Math.floor(index / this._rangeLength);
        if (numLooped === 0) {
            return color;
        }
        var modifyFactor = Math.log(numLooped * Color._LOOP_LIGHTEN_FACTOR + 1);
        return Utils.Color.lightenColor(color, modifyFactor);
    };
    Color.prototype._getDomain = function () {
        return this._backingScaleDomain();
    };
    Color.prototype._backingScaleDomain = function (values) {
        if (values == null) {
            return this._d3Scale.domain();
        }
        else {
            this._d3Scale.domain(values);
            this._tracker.clear();
            return this;
        }
    };
    Color.prototype._getRange = function () {
        return this._d3Scale.range();
    };
    Color.prototype._setRange = function (values) {
        this._d3Scale.range(values);
        this._rangeLength = values.length;
    };
    Color._LOOP_LIGHTEN_FACTOR = 1.6;
    // The maximum number of colors we are getting from CSS stylesheets
    Color._MAXIMUM_COLORS_FROM_CSS = 256;
    return Color;
}(scale_1.Scale));
exports.Color = Color;
